import { useEffect, useState } from "react";
import { api, timeShort } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, ChevronLeft, ChevronRight, Trash2, Lock, LockOpen, Cloud } from "lucide-react";
import { toast } from "sonner";

const COLORS = ["blue","purple","green","amber","red"];
const KINDS = ["meeting","block","focus","personal"];

function startOfWeek(d) { const x = new Date(d); const day = (x.getDay() + 6) % 7; x.setDate(x.getDate() - day); x.setHours(0,0,0,0); return x; }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }

export default function CalendarPage() {
  const [anchor, setAnchor] = useState(new Date());
  const [events, setEvents] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const week = [...Array(7)].map((_, i) => addDays(startOfWeek(anchor), i));

  const load = async () => {
    const start = week[0].toISOString();
    const end = new Date(week[6].getTime() + 24*3600e3 - 1).toISOString();
    const { data } = await api.get("/calendar", { params: { start, end } });
    setEvents(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [anchor]);

  const save = async (form) => {
    try {
      if (editing?.id) await api.put(`/calendar/${editing.id}`, form);
      else await api.post("/calendar", form);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch { toast.error("Save failed"); }
  };
  const remove = async (id) => { await api.delete(`/calendar/${id}`); load(); };

  const syncGoogle = async () => {
    try {
      const { data } = await api.post("/google/calendar/sync");
      toast.success(`Synced ${data.inserted} new + ${data.updated} updated events from Google`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Google sync failed. Connect in Settings → Google.");
    }
  };

  const eventsByDay = (day) => events.filter((e) => new Date(e.start).toDateString() === day.toDateString());

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Calendar</h1>
          <p className="text-sm text-muted-foreground mt-1">{"Blocks, meetings and focus time — lock events so the day planner won't move them."}</p>
        </div>
        <div className="md:ml-auto flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => setAnchor(addDays(anchor, -7))}><ChevronLeft className="w-4 h-4"/></Button>
          <div className="text-sm font-medium min-w-[180px] text-center">
            {week[0].toLocaleDateString(undefined, { month: "short", day: "numeric" })} — {week[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </div>
          <Button variant="outline" size="icon" onClick={() => setAnchor(addDays(anchor, 7))}><ChevronRight className="w-4 h-4"/></Button>
          <Button variant="outline" onClick={() => setAnchor(new Date())}>Today</Button>
          <Button data-testid="google-sync-btn" variant="outline" onClick={syncGoogle}><Cloud className="w-4 h-4 mr-2"/> Sync Google</Button>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild><Button data-testid="add-event-btn" onClick={() => setEditing({})}><Plus className="w-4 h-4 mr-2"/> Event</Button></DialogTrigger>
            <EventDialog event={editing} onSave={save}/>
          </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
        {week.map((day) => (
          <Card key={day.toISOString()} className="studio-card p-3 min-h-[220px]">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{day.toLocaleDateString(undefined, { weekday: "short" })}</div>
            <div className="text-xl font-semibold">{day.getDate()}</div>
            <div className="mt-3 space-y-1.5">
              {eventsByDay(day).length === 0 && <div className="text-[11px] text-muted-foreground">—</div>}
              {eventsByDay(day).map((e) => (
                <div key={e.id} className="studio-schedule-block group" data-color={e.color}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-xs font-medium truncate">{e.title}</div>
                    <button onClick={() => remove(e.id)} className="opacity-0 group-hover:opacity-100 transition-opacity"><Trash2 className="w-3 h-3"/></button>
                  </div>
                  <div className="text-[10px] text-muted-foreground flex items-center gap-1">
                    {e.is_locked ? <Lock className="w-2.5 h-2.5"/> : <LockOpen className="w-2.5 h-2.5 opacity-40"/>}
                    {timeShort(e.start)}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function EventDialog({ event, onSave }) {
  const [f, setF] = useState({});
  useEffect(() => {
    const now = new Date();
    now.setMinutes(0,0,0);
    const later = new Date(now.getTime() + 3600e3);
    setF({
      title: event?.title || "",
      description: event?.description || "",
      start: event?.start ? event.start.slice(0,16) : now.toISOString().slice(0,16),
      end: event?.end ? event.end.slice(0,16) : later.toISOString().slice(0,16),
      location: event?.location || "",
      is_locked: event?.is_locked || false,
      color: event?.color || "blue",
      kind: event?.kind || "meeting",
    });
  }, [event]);
  return (
    <DialogContent className="max-w-lg">
      <DialogHeader><DialogTitle>{event?.id ? "Edit event" : "New event"}</DialogTitle></DialogHeader>
      <div className="space-y-3">
        <div className="space-y-1.5"><Label className="text-xs">Title</Label><Input data-testid="event-title" value={f.title || ""} onChange={(e) => setF({...f, title: e.target.value})}/></div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Start</Label><Input type="datetime-local" value={f.start || ""} onChange={(e) => setF({...f, start: e.target.value})}/></div>
          <div className="space-y-1.5"><Label className="text-xs">End</Label><Input type="datetime-local" value={f.end || ""} onChange={(e) => setF({...f, end: e.target.value})}/></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div className="space-y-1.5"><Label className="text-xs">Type</Label>
            <Select value={f.kind} onValueChange={(v) => setF({...f, kind: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Color</Label>
            <Select value={f.color} onValueChange={(v) => setF({...f, color: v})}>
              <SelectTrigger><SelectValue/></SelectTrigger>
              <SelectContent>{COLORS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label className="text-xs">Locked?</Label>
            <div className="flex items-center gap-2 h-10"><Switch data-testid="event-lock" checked={!!f.is_locked} onCheckedChange={(v) => setF({...f, is_locked: v})}/> <span className="text-xs text-muted-foreground">{"Don't reschedule"}</span></div>
          </div>
        </div>
        <div className="space-y-1.5"><Label className="text-xs">Location</Label><Input value={f.location || ""} onChange={(e) => setF({...f, location: e.target.value})}/></div>
        <div className="space-y-1.5"><Label className="text-xs">Description</Label><Textarea rows={2} value={f.description || ""} onChange={(e) => setF({...f, description: e.target.value})}/></div>
      </div>
      <DialogFooter><Button data-testid="event-save" onClick={() => {
        onSave({
          ...f,
          start: new Date(f.start).toISOString(),
          end: new Date(f.end).toISOString(),
        });
      }}>Save</Button></DialogFooter>
    </DialogContent>
  );
}
